import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import { cn } from '../../utils/cn';
import type { NavItem as NavItemType } from '../../types/navigation';

interface NavItemProps {
  item: NavItemType;
}

export function NavItem({ item }: NavItemProps) {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const isActive = location.pathname === item.path || location.pathname.startsWith(item.path + '/');
  const hasChildren = item.children && item.children.length > 0;

  // Close dropdown when clicking outside
  useEffect(() => {
    if (!isOpen) return;

    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Close dropdown on escape key
  useEffect(() => {
    if (!isOpen) return;

    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  if (hasChildren) {
    return (
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          data-testid={item.testId || `nav-item-${item.key}`}
          className={cn(
            'inline-flex items-center gap-1 px-3 py-2 text-sm font-medium rounded-md transition-colors',
            isActive
              ? 'text-gray-900 bg-gray-100 dark:text-slate-100 dark:bg-slate-800'
              : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800'
          )}
          aria-expanded={isOpen}
          aria-haspopup="true"
        >
          {item.label}
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform duration-200',
              isOpen && 'rotate-180'
            )}
          />
        </button>

        {/* Dropdown menu */}
        {isOpen && (
          <div
            className="absolute left-0 top-full mt-1 w-40 py-1 bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-slate-900 dark:ring-slate-700 z-50"
            role="menu"
            aria-orientation="vertical"
          >
            {item.children!.map((child) => {
              const isChildActive = location.pathname === child.path;
              return (
                <Link
                  key={child.key}
                  to={child.path}
                  data-testid={`nav-item-${child.key}`}
                  onClick={() => setIsOpen(false)}
                  className={cn(
                    'block px-4 py-2 text-sm transition-colors',
                    isChildActive
                      ? 'bg-gray-100 text-gray-900 font-medium dark:bg-slate-800 dark:text-slate-100'
                      : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-100'
                  )}
                  role="menuitem"
                >
                  {child.label}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.path}
      data-testid={item.testId || `nav-item-${item.key}`}
      className={cn(
        'inline-flex items-center px-3 py-2 text-sm font-medium rounded-md transition-colors',
        isActive
          ? 'text-gray-900 bg-gray-100 dark:text-slate-100 dark:bg-slate-800'
          : 'text-gray-700 hover:text-gray-900 hover:bg-gray-50 dark:text-slate-400 dark:hover:text-slate-100 dark:hover:bg-slate-800'
      )}
    >
      {item.label}
      {item.badge === 'preview' && (
        <span className="ml-1 inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-gray-100 text-gray-700 dark:bg-slate-700 dark:text-slate-300">
          Preview
        </span>
      )}
    </Link>
  );
}
